import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, addDoc, serverTimestamp, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { 
  Users, 
  Wifi, 
  Star, 
  MapPin, 
  X,
  FileText,
  Send,
  ExternalLink
} from 'lucide-react';
import SEO from '../components/SEO';

// Type definitions
interface ApplicationData {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt?: {
    seconds: number;
  };
  updatedAt?: {
    seconds: number;
  };
  [key: string]: any;
}

const Servers: React.FC = () => {
  const { currentUser } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [servers, setServers] = useState<any[]>([]);
  const [serverSettings, setServerSettings] = useState<any>(null);
  const [userApplication, setUserApplication] = useState<ApplicationData | null>(null);
  const [checkingApplication, setCheckingApplication] = useState(false);
  
  const [formData, setFormData] = useState({
    // Roleplay soruları
    failRp: '',
    metaGaming: '',
    powerGaming: '',
    combatLogging: '',
    rageQuit: '',
    // Oyun deneyimi
    gameExperience: '',
    roleplayExperience: '',
    characterName: '',
    characterStory: '',
    characterGoal: ''
  });

  // Kullanıcının başvuru durumunu kontrol et
  useEffect(() => {
    const checkUserApplication = async () => {
      if (!currentUser) {
        setCheckingApplication(false);
        setUserApplication(null);
        return;
      }

      try {
        setCheckingApplication(true);
        const applicationsQuery = query(
          collection(db, 'forms'),
          where('userId', '==', currentUser.uid)
        );
        const applicationsSnapshot = await getDocs(applicationsQuery);
        
        if (!applicationsSnapshot.empty) {
          // En son başvuruyu bul (client-side sorting)
          const applications: ApplicationData[] = applicationsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          } as ApplicationData));
          
          // createdAt'e göre sırala (en yeni en üstte)
          applications.sort((a, b) => {
            const aTime = a.createdAt?.seconds || 0;
            const bTime = b.createdAt?.seconds || 0;
            return bTime - aTime;
          });
          
          const latestApplication = applications[0];
          setUserApplication(latestApplication);
        } else {
          setUserApplication(null);
        }
      } catch (error) {
        // Hata yönetimi - loglanmaz güvenlik nedeniyle
        setUserApplication(null);
      } finally {
        setCheckingApplication(false);
      }
    };

    checkUserApplication();
  }, [currentUser]);

  // Server ayarlarını yükle
  useEffect(() => {
    const loadServerSettings = async () => {
      // Varsayılan ayarlar
      const defaultSettings = {
        id: 'default',
        discordLink: 'https://discord.gg/aedonv',
        applicationStatusText: 'Formunuz Kontrol Aşamasındadır',
        serverName: 'Aedon Community',
        serverDescription: 'En iyi FiveM sunucu deneyimi için buradayız!'
      };
      
      // Varsayılan sunucular (boş liste durumunda kullanılacak)
      const defaultServers = [
        {
          id: 1,
          name: 'Aedon V Roleplay',
          owner: 'Aedon Community',
          category: 'roleplay',
          description: 'Gerçekçi roleplay deneyimi için tasarlanmış premium sunucu',
          players: 5,
          maxPlayers: 150,
          status: 'online',
          ip: 'https://servers.fivem.net/servers/detail/9vdy3y',
          discordLink: 'https://discord.gg/aedonv',
          features: ['QBCore Framework', 'Custom Jobs', 'Housing System', 'Economy'],
          rating: 4.8,
          uptime: '99.9%',
          location: 'Türkiye'
        },
        {
          id: 2,
          name: 'UniQ V Roleplay',
          owner: 'UniQ Community',
          category: 'roleplay',
          description: 'Gerçekçi roleplay deneyimi için tasarlanmış premium sunucu',
          players: 5,
          maxPlayers: 150,
          status: 'online',
          ip: 'Henüz Aktif Değil',
          discordLink: 'https://discord.gg/uniqu',
          features: ['QBCore Framework', 'Custom Jobs', 'Housing System', 'Economy'],
          rating: 4.6,
          uptime: '99.9%',
          location: 'Türkiye'
        },
        {
          id: 3,
          name: 'Aedon V Racing',
          owner: 'Aedon Racing Team',
          category: 'racing',
          description: 'Hızlı araçlar ve adrenalin dolu yarışlar için tasarlanmış özel sunucu',
          players: 0,
          maxPlayers: 100,
          status: 'online',
          ip: 'Henüz Aktif Değil',
          discordLink: 'https://discord.gg/aedonracing',
          features: ['Drift Tracks', 'Car Tuning', 'Competitions', 'Replay System'],
          rating: 4.4,
          uptime: '96.9%',
          location: 'Türkiye'
        }
      ];

      try {
        // Servers koleksiyonundan verileri yükle (giriş gerekmeden erişilebilir olmalı)
        const serversRef = collection(db, 'servers');
        const serversSnapshot = await getDocs(serversRef);
        
        const serversList: any[] = [];
        serversSnapshot.forEach((doc) => {
          serversList.push({ id: doc.id, ...doc.data() });
        });
        
        // Server ayarlarını yükle
        let settingsData = {
          ...defaultSettings,
          servers: serversList.length > 0 ? serversList : defaultServers
        };
        
        try {
          const settingsQuery = query(collection(db, 'serverSettings'));
          const settingsSnapshot = await getDocs(settingsQuery);
          
          if (!settingsSnapshot.empty) {
            const existingSettings = settingsSnapshot.docs[0].data();
            settingsData = { 
              id: settingsSnapshot.docs[0].id,
              discordLink: existingSettings.discordLink || defaultSettings.discordLink,
              applicationStatusText: existingSettings.applicationStatusText || defaultSettings.applicationStatusText,
              serverName: existingSettings.serverName || defaultSettings.serverName,
              serverDescription: existingSettings.serverDescription || defaultSettings.serverDescription,
              servers: serversList.length > 0 ? serversList : defaultServers
            };
          }
        } catch (settingsError) {
          // Server settings yüklenemezse varsayılanları kullan, ama sunucuları göster
          // Hata loglanmaz - production güvenliği için
        }
        
        setServerSettings(settingsData);
        // Firestore'dan gelen sunucular varsa onları kullan, yoksa varsayılanları göster
        setServers(serversList.length > 0 ? serversList : defaultServers);
      } catch (error) {
        // Hata durumunda varsayılan ayarları ve sunucuları kullan
        setServerSettings({
          ...defaultSettings,
          servers: defaultServers
        });
        setServers(defaultServers);
      }
    };

    loadServerSettings();
  }, []);

  // Sunucu verilerini gerçek zamanlı güncelle
  useEffect(() => {
    // Varsayılan sunucular (boş liste durumunda kullanılacak)
    const defaultServers: any []= [
      /* {
        id: 1,
        name: 'Aedon V Roleplay',
        owner: 'Aedon Community',
        category: 'roleplay',
        description: 'Gerçekçi roleplay deneyimi için tasarlanmış premium sunucu',
        players: 5,
        maxPlayers: 150,
        status: 'online',
        ip: 'https://servers.fivem.net/servers/detail/9vdy3y',
        discordLink: 'https://discord.gg/aedonv',
        features: ['QBCore Framework', 'Custom Jobs', 'Housing System', 'Economy'],
        rating: 4.8,
        uptime: '99.9%',
        location: 'Türkiye'
      },
      {
        id: 2,
        name: 'UniQ V Roleplay',
        owner: 'UniQ Community',
        category: 'roleplay',
        description: 'Gerçekçi roleplay deneyimi için tasarlanmış premium sunucu',
        players: 5,
        maxPlayers: 150,
        status: 'online',
        ip: 'Henüz Aktif Değil',
        discordLink: 'https://discord.gg/uniqu',
        features: ['QBCore Framework', 'Custom Jobs', 'Housing System', 'Economy'],
        rating: 4.6,
        uptime: '99.9%',
        location: 'Türkiye'
      },
      {
        id: 3,
        name: 'Aedon V Racing',
        owner: 'Aedon Racing Team',
        category: 'racing',
        description: 'Hızlı araçlar ve adrenalin dolu yarışlar için tasarlanmış özel sunucu',
        players: 0,
        maxPlayers: 100,
        status: 'online',
        ip: 'Henüz Aktif Değil',
        discordLink: 'https://discord.gg/aedonracing',
        features: ['Drift Tracks', 'Car Tuning', 'Competitions', 'Replay System'],
        rating: 4.4,
        uptime: '96.9%',
        location: 'Türkiye'
      } */
    ];

    const unsubscribeServers = onSnapshot(
      collection(db, 'servers'), 
      (snapshot) => {
        const serversList: any[] = [];
        snapshot.forEach((doc) => {
          serversList.push({ id: doc.id, ...doc.data() });
        });
        
        // Firestore'dan gelen sunucular varsa onları kullan, yoksa varsayılanları göster
        const finalServers = serversList.length > 0 ? serversList : defaultServers;
        setServers(finalServers);
        
        // Server settings'i de güncelle
        if (serverSettings) {
          setServerSettings((prev: any) => ({
            ...prev,
            servers: finalServers
          }));
        }
      },
      (error) => {
        // Hata durumunda varsayılan sunucuları göster
        // Hata loglanmaz - production güvenliği için
        setServers(defaultServers);
        if (serverSettings) {
          setServerSettings((prev: any) => ({
            ...prev,
            servers: defaultServers
          }));
        }
      }
    );

    return () => unsubscribeServers();
  }, [serverSettings]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    
    setIsSubmitting(true);
    
    try {
      const applicationData = {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: currentUser.displayName || 'Anonim',
        ...formData,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await addDoc(collection(db, 'forms'), applicationData);
      
      // Başvuru durumunu tekrar kontrol et
      const applicationsQuery = query(
        collection(db, 'forms'),
        where('userId', '==', currentUser.uid)
      );
      const applicationsSnapshot = await getDocs(applicationsQuery);
      
      if (!applicationsSnapshot.empty) {
        // En son başvuruyu bul (client-side sorting)
        const applications: ApplicationData[] = applicationsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as ApplicationData));
        
        // createdAt'e göre sırala (en yeni en üstte)
        applications.sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return bTime - aTime;
        });
        
        const latestApplication = applications[0];
        setUserApplication(latestApplication);
      }
      
      setIsSubmitting(false);
      setSubmitStatus('success');
      
      // Form verilerini sıfırla
      setFormData({
        failRp: '',
        metaGaming: '',
        powerGaming: '',
        combatLogging: '',
        rageQuit: '',
        gameExperience: '',
        roleplayExperience: '',
        characterName: '',
        characterStory: '',
        characterGoal: ''
      });
      
      setTimeout(() => {
        setSubmitStatus('idle');
        setIsFormOpen(false);
      }, 2000);
    } catch (error) {
      // Hata yönetimi - loglanmaz güvenlik nedeniyle
      setIsSubmitting(false);
      setSubmitStatus('error');
      
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }
  };

  const categories = [
    { id: 'all', label: 'Tümü' },
    { id: 'roleplay', label: 'Roleplay' },
    { id: 'racing', label: 'Yarış' },
    { id: 'survival', label: 'Hayatta Kalma' },
    { id: 'freeroam', label: 'Serbest Oyun' }
  ];

  const filteredServers = selectedCategory === 'all' 
    ? servers 
    : servers.filter(server => server.category === selectedCategory);

  return (
    <>
      <SEO 
        title="Sunucularımız - FiveM Servers | Aedon Community" 
        description="En iyi FiveM sunucularımızı keşfedin. Roleplay, yarış, hayatta kalma ve daha fazlası için özel sunucularımız."
        keywords="FiveM, sunucu, roleplay, yarış, hayatta kalma, Aedon Community"
      />
      
       <div className="min-h-screen bg-dark-900 text-white pt-24">
        {/* Header */}
         <div className="bg-dark-900 py-20">
           <div className="container mx-auto px-4 text-center">
             <h1 className="text-4xl md:text-6xl font-gaming font-bold mb-6">
               Sunucularımız
          </h1>
             <p className="text-xl text-gray-300 max-w-3xl mx-auto">
               En iyi FiveM sunucu deneyimi için özel olarak tasarlanmış sunucularımızı keşfedin
             </p>
           </div>
         </div>

         {/* Category Filter */}
         <div className="container mx-auto px-4 py-12">
          <div className="flex flex-wrap justify-center gap-4 mb-8">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                selectedCategory === category.id
                   ? 'bg-white text-dark-900 shadow-lg'
                     : 'bg-dark-700 text-gray-300 hover:bg-dark-600'
              }`}
            >
              {category.label}
            </button>
          ))}
          </div>

           {/* Server Cards */}
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
             {filteredServers.map((server) => (
            <motion.div
              key={server.id}
               initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.5 }}
               className="bg-dark-800 rounded-xl border border-dark-700 hover:border-white transition-all duration-300 overflow-hidden shadow-xl hover:shadow-2xl"
            >
              {/* Server Header */}
               <div className="p-6">
                 <div className="flex justify-between items-start mb-6">
                   <div className="flex-1">
                     <h3 className="text-2xl font-bold text-white mb-2">{server.name}</h3>
                     {server.owner && (
                       <p className="text-primary-400 text-sm font-medium mb-3 flex items-center">
                         <span className="mr-1">👑</span>
                         {server.owner}
                       </p>
                     )}
                     <p className="text-gray-300 text-sm leading-relaxed">{server.description}</p>
                </div>
                   <div className="flex items-center space-x-2 ml-4">
                  <div className={`w-3 h-3 rounded-full ${
                       server.status === 'online' ? 'bg-green-500 animate-pulse' : 
                       server.status === 'offline' ? 'bg-red-500' : 'bg-yellow-500'
                  }`}></div>
                     <span className="text-xs text-gray-400 capitalize font-medium">{server.status}</span>
                </div>
              </div>

              {/* Server Stats */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                   <div className="flex items-center space-x-3 p-3 bg-dark-700 rounded-lg">
                     <Users className="w-5 h-5 text-primary-400" />
                     <div>
                       <p className="text-xs text-gray-400">Oyuncular</p>
                       <p className="text-sm font-semibold text-white">
                         {server.players || 0}/{server.maxPlayers || 100}
                       </p>
                     </div>
                   </div>
                   <div className="flex items-center space-x-3 p-3 bg-dark-700 rounded-lg">
                     <Star className="w-5 h-5 text-yellow-400 fill-current" />
                     <div>
                       <p className="text-xs text-gray-400">Rating</p>
                       <p className="text-sm font-semibold text-white">{server.rating || '5.0'}</p>
                     </div>
                   </div>
                   <div className="flex items-center space-x-3 p-3 bg-dark-700 rounded-lg">
                     <Wifi className="w-5 h-5 text-primary-400" />
                     <div>
                       <p className="text-xs text-gray-400">Uptime</p>
                       <p className="text-sm font-semibold text-white">{server.uptime || '100%'}</p>
                </div>
                </div>
                   <div className="flex items-center space-x-3 p-3 bg-dark-700 rounded-lg">
                     <MapPin className="w-5 h-5 text-primary-400" />
                     <div>
                       <p className="text-xs text-gray-400">Konum</p>
                       <p className="text-sm font-semibold text-white">{server.location || 'Türkiye'}</p>
                </div>
                </div>
              </div>

              {/* Features */}
                 {server.features && server.features.length > 0 && (
              <div className="mb-6">
                     <h4 className="text-sm font-medium text-gray-400 mb-3">Özellikler</h4>
                <div className="flex flex-wrap gap-2">
                       {server.features.slice(0, 3).map((feature: string, index: number) => (
                         <span key={index} className="px-3 py-1 bg-primary-500/20 text-primary-300 text-xs rounded-full border border-primary-500/30">
                      {feature}
                    </span>
                  ))}
                       {server.features.length > 3 && (
                         <span className="px-3 py-1 bg-dark-600 text-gray-400 text-xs rounded-full border border-dark-500">
                           +{server.features.length - 3} daha
                         </span>
                       )}
                </div>
              </div>
                 )}

                 {/* Application Section */}
                 <div className="bg-dark-700 rounded-lg p-6 border border-dark-600">
                   <div className="flex items-center justify-between">
                <div className="flex-1">
                       <h4 className="text-sm font-medium text-gray-300 mb-2">Sunucuya Katılmak İçin:</h4>
                       {checkingApplication ? (
                         <div className="flex items-center space-x-2">
                           <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
                           <p className="text-white font-medium">Kontrol ediliyor...</p>
                         </div>
                       ) : userApplication ? (
                         userApplication.status === 'approved' ? (
                           <p className="text-green-400 font-semibold flex items-center">
                             <span className="mr-2">✅</span>
                             Başvurunuz Onaylandı!
                           </p>
                         ) : userApplication.status === 'rejected' ? (
                           <p className="text-red-400 font-semibold flex items-center">
                             <span className="mr-2">❌</span>
                             Başvurunuz Reddedildi
                           </p>
                         ) : (
                           <p className="text-yellow-400 font-semibold flex items-center">
                             <span className="mr-2">⏳</span>
                             {serverSettings?.applicationStatusText || 'Formunuz Kontrol Aşamasındadır'}
                           </p>
                         )
                       ) : (
                         <p className="text-white font-semibold flex items-center">
                           <span className="mr-2">📝</span>
                           Form Doldurun
                         </p>
                       )}
                     </div>
                     <div className="ml-4">
                       {!currentUser ? (
                         <div className="text-center">
                           <p className="text-sm text-gray-400 mb-3">Giriş yapın</p>
                           <button
                             onClick={() => window.location.href = '/login'}
                             className="bg-white hover:bg-gray-200 text-dark-900 px-6 py-3 rounded-lg font-medium transition-all duration-300 hover:scale-105 shadow-lg"
                           >
                             Giriş Yap
                           </button>
                         </div>
                       ) : checkingApplication ? (
                         <div className="text-center">
                           <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-3"></div>
                           <p className="text-sm text-gray-400">Kontrol ediliyor...</p>
                </div>
                       ) : userApplication ? (
                         userApplication.status === 'approved' ? (
                           <a
                             href={server.discordLink || serverSettings?.discordLink || 'https://discord.gg/aedonv'}
                             target="_blank"
                             rel="noopener noreferrer"
                             className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-all duration-300 hover:scale-105 shadow-lg flex items-center"
                           >
                             <ExternalLink className="w-5 h-5 mr-2" />
                             Discord'a Katıl
                           </a>
                         ) : (
                           <div className="text-center">
                             <p className="text-sm text-gray-400 mb-3">
                               {userApplication.status === 'pending' 
                                 ? 'Başvurunuz değerlendiriliyor' 
                                 : 'Başvurunuz reddedildi'
                               }
                             </p>
                             {userApplication.status === 'rejected' && (
                  <button
                                 onClick={() => setIsFormOpen(true)}
                                 className="bg-yellow-600 hover:bg-yellow-700 text-dark-900 px-6 py-3 rounded-lg font-medium transition-all duration-300 hover:scale-105 shadow-lg"
                  >
                                 Tekrar Başvur
                  </button>
                             )}
                           </div>
                         )
                       ) : (
                  <button
                           onClick={() => setIsFormOpen(true)}
                           className="bg-white hover:bg-gray-200 text-dark-900 px-6 py-3 rounded-lg font-medium transition-all duration-300 hover:scale-105 shadow-lg flex items-center"
                  >
                           <FileText className="w-5 h-5 mr-2" />
                           Form Doldur
                  </button>
                       )}
                     </div>
                   </div>
                </div>
              </div>
            </motion.div>
          ))}
           </div>
        </div>

        {/* Application Form Modal */}
        <AnimatePresence>
          {isFormOpen && !userApplication && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="bg-dark-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              >
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-white">Sunucu Başvuru Formu</h2>
                  <button
                    onClick={() => setIsFormOpen(false)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleFormSubmit} className="space-y-6">
                  {/* Roleplay Soruları */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">Roleplay Soruları</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Fail RP nedir? Örnek verin.
                        </label>
                        <textarea
                          name="failRp"
                          value={formData.failRp}
                          onChange={handleInputChange}
                          rows={3}
                          className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded text-white text-sm resize-none"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Meta Gaming nedir? Nasıl önlenir?
                        </label>
                        <textarea
                          name="metaGaming"
                          value={formData.metaGaming}
                          onChange={handleInputChange}
                          rows={3}
                          className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded text-white text-sm resize-none"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Power Gaming nedir? Örnek verin.
                        </label>
                        <textarea
                          name="powerGaming"
                          value={formData.powerGaming}
                          onChange={handleInputChange}
                          rows={3}
                          className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded text-white text-sm resize-none"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Combat Logging nedir? Neden yasaktır?
                        </label>
                        <textarea
                          name="combatLogging"
                          value={formData.combatLogging}
                          onChange={handleInputChange}
                          rows={3}
                          className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded text-white text-sm resize-none"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Rage Quit nedir? Ne zaman yapılabilir?
                        </label>
                        <textarea
                          name="rageQuit"
                          value={formData.rageQuit}
                          onChange={handleInputChange}
                          rows={3}
                          className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded text-white text-sm resize-none"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Oyun Deneyimi */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">Oyun Deneyimi</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          FiveM deneyiminiz nedir?
                        </label>
                        <textarea
                          name="gameExperience"
                          value={formData.gameExperience}
                          onChange={handleInputChange}
                          rows={3}
                          className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded text-white text-sm resize-none"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Roleplay deneyiminiz nedir?
                        </label>
                        <textarea
                          name="roleplayExperience"
                          value={formData.roleplayExperience}
                          onChange={handleInputChange}
                          rows={3}
                          className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded text-white text-sm resize-none"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Karakter Bilgileri */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">Karakter Bilgileri</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Karakter Adı
                        </label>
                        <input
                          type="text"
                          name="characterName"
                          value={formData.characterName}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded text-white text-sm"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Karakter Hikayesi
                        </label>
                        <textarea
                          name="characterStory"
                          value={formData.characterStory}
                          onChange={handleInputChange}
                          rows={4}
                          className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded text-white text-sm resize-none"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Karakterinizin Amacı
                        </label>
                        <textarea
                          name="characterGoal"
                          value={formData.characterGoal}
                          onChange={handleInputChange}
                          rows={3}
                          className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded text-white text-sm resize-none"
                          required
                        />
                      </div>
      </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end space-x-4 pt-4">
                    <button
                      type="button"
                      onClick={() => setIsFormOpen(false)}
                      className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                    >
                      İptal
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-6 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Gönderiliyor...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Başvuruyu Gönder
                        </>
                      )}
                    </button>
                  </div>
                </form>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="mt-4 p-4 bg-green-900/20 border border-green-500 rounded-lg text-green-400 text-center">
                    Başvurunuz başarıyla gönderildi! En kısa sürede değerlendirilecektir.
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="mt-4 p-4 bg-red-900/20 border border-red-500 rounded-lg text-red-400 text-center">
                    Başvuru gönderilirken bir hata oluştu. Lütfen tekrar deneyin.
                  </div>
                )}
              </motion.div>
            </div>
          )}
        </AnimatePresence>
    </div>
    </>
  );
};

export default Servers; 